/**
 * Modal "Datos para transferir" del portal vecino — Sprint 15 S15-01.
 *
 * Muestra la cuenta bancaria del barrio + cartel crítico sobre el
 * correo de destino + ejemplos del mensaje que el vecino debe poner
 * en la transferencia para que el parser auto-distribuya el pago.
 *
 * Puntos de entrada (accesibilidad desde cualquier página del portal):
 * 1. UserMenu (avatar dropdown del Header) → item "Datos para transferir".
 * 2. Dashboard del vecino → botón "Cómo pagar" en card "Últimos
 *    movimientos" (solo visible cuando hay saldo pendiente).
 *
 * Estados:
 * - loading: spinner mientras fetchea la cuenta.
 * - 404: cuenta NO configurada todavía. Muestra mensaje claro al vecino
 *   pidiendo que contacte a la Comisión (porque solo el super_admin la
 *   puede completar desde sa.calbucura.cl).
 * - 200: 6 datos + cartel + ejemplos.
 */
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  getCuentaBancariaBarrio,
  type CuentaBancariaBarrio,
} from '../../lib/api/barrio';
import { formatRut } from '../../lib/format';
import { Modal } from './Modal';

const LABEL_TIPO_CUENTA: Record<CuentaBancariaBarrio['tipo'], string> = {
  corriente: 'Cuenta Corriente',
  vista: 'Cuenta Vista',
  ahorro: 'Cuenta de Ahorro',
};

interface DatosCuentaModalProps {
  /** Llamado cuando el usuario cierra (ESC, X, click backdrop o tap fuera). */
  onClose: () => void;
  /** Override de la cuenta para previews dev-only (`/dev/datos-cuenta`).
   *  Si está, salta el fetch al endpoint. Sprint 15 S15-01. */
  cuentaOverride?: CuentaBancariaBarrio;
  /** Forzar el estado loading en previews dev-only. */
  forceLoading?: boolean;
  /** Forzar el estado 404 (cuenta no configurada) en previews dev-only. */
  forceNotFound?: boolean;
}

interface FilaProps {
  label: string;
  value: string;
  mono?: boolean;
}

function Fila({ label, value, mono }: FilaProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 py-1.5 border-b border-white/[0.05] last:border-0">
      <div className="text-xs uppercase tracking-wide text-text-muted sm:w-32 sm:shrink-0">
        {label}
      </div>
      <div
        className={`text-sm text-text-primary ${mono ? 'font-mono' : ''} break-all`}
      >
        {value}
      </div>
    </div>
  );
}

export function DatosCuentaModal({
  onClose,
  cuentaOverride,
  forceLoading,
  forceNotFound,
}: DatosCuentaModalProps) {
  const [cuenta, setCuenta] = useState<CuentaBancariaBarrio | null>(
    cuentaOverride ?? null,
  );
  const [loading, setLoading] = useState(!cuentaOverride && !forceNotFound);
  const [errorMsg, setErrorMsg] = useState<string | null>(
    forceNotFound
      ? 'La cuenta bancaria del barrio aún no está configurada. ' +
          'Contacta a la Comisión para que la complete antes de ' +
          'realizar tu transferencia.'
      : null,
  );

  useEffect(() => {
    // Modo preview: data viene como prop, saltamos el fetch.
    if (cuentaOverride || forceLoading || forceNotFound) {
      setLoading(forceLoading === true);
      return;
    }
    let cancelado = false;
    setLoading(true);
    setErrorMsg(null);
    getCuentaBancariaBarrio()
      .then((data) => {
        if (!cancelado) setCuenta(data);
      })
      .catch((err) => {
        if (cancelado) return;
        if (err instanceof ApiError && err.status === 404) {
          setErrorMsg(
            'La cuenta bancaria del barrio aún no está configurada. ' +
              'Contacta a la Comisión para que la complete antes de ' +
              'realizar tu transferencia.',
          );
        } else {
          setErrorMsg(
            'No pudimos cargar los datos de la cuenta. Intenta cerrar ' +
              'este aviso y abrirlo de nuevo.',
          );
        }
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [cuentaOverride, forceLoading, forceNotFound]);

  return (
    <Modal title="Datos para transferir" onClose={onClose}>
      {loading && (
        <p className="text-sm text-text-muted py-4 text-center">
          Cargando datos de la cuenta…
        </p>
      )}

      {!loading && errorMsg && (
        <div className="bg-state-warning/15 border-l-4 border-state-warning p-3 rounded-sm text-sm text-text-primary">
          {errorMsg}
        </div>
      )}

      {!loading && cuenta && (
        <div className="space-y-5">
          <section className="bg-bg-sunken/60 rounded-md p-3 sm:p-4">
            <Fila label="Banco" value={cuenta.banco} />
            <Fila label="Tipo Cuenta" value={LABEL_TIPO_CUENTA[cuenta.tipo]} />
            <Fila label="N° cuenta" value={cuenta.numero} mono />
            <Fila label="Nombre" value={cuenta.titular_nombre} />
            <Fila label="RUT" value={formatRut(cuenta.titular_rut)} mono />
            <Fila label="Correo" value={cuenta.correo_destino} mono />
          </section>

          <section className="bg-state-warning/10 border-l-4 border-state-warning rounded-sm p-3 space-y-2 text-sm text-text-primary">
            <div className="font-medium">
              ⚠ Importante para el procesamiento automático
            </div>
            <p className="text-text-secondary">
              Al configurar la transferencia en tu banca móvil, ingresa{' '}
              <span className="font-mono text-orange">
                {cuenta.correo_destino}
              </span>{' '}
              como <strong>correo del destinatario</strong>. Tu banco enviará
              el comprobante automáticamente a esa dirección.
            </p>
            <p className="text-text-secondary">
              En el <strong>mensaje al destinatario</strong> indica tu casa y
              el motivo:
            </p>
            <ul className="text-xs text-text-secondary space-y-0.5 pl-3 font-mono">
              <li>• Casa ####, cuota</li>
              <li>• Casa ####, control</li>
              <li>• Casa ####, llavero</li>
              <li>• Casa ####, multa</li>
            </ul>
            <p className="text-xs text-text-muted pt-1">
              Si el comprobante no llega al correo o el mensaje no identifica
              tu casa, el pago quedará pendiente de clasificación manual hasta
              que la tesorería lo procese.
            </p>
          </section>
        </div>
      )}
    </Modal>
  );
}
